// Resolving the registry for one entity type: its type chain, inherited fields, the active profile template
// (with variant inheritance), colors and ID codes (PLAN §2 "Profile templates", §12.2).

import { foldText } from './match';
import type { EntityTypeDef, FieldDef, FieldGroupDef, ProfileTemplateDef, SchemaSnapshot, TemplateBlock } from './types';

type Snap = Pick<SchemaSnapshot, 'types' | 'groups' | 'fields' | 'templates'>;

/** The type, then its parent, grandparent… (cycle-safe). */
export function typeChain(types: readonly EntityTypeDef[], typeId: string): EntityTypeDef[] {
  const out: EntityTypeDef[] = [];
  const seen = new Set<string>();
  let t = types.find(x => x.id === typeId);
  while (t && !seen.has(t.id)) {
    out.push(t);
    seen.add(t.id);
    t = t.parentTypeId ? types.find(x => x.id === t?.parentTypeId) : undefined;
  }
  return out;
}

/** Sub-types without a color inherit their parent's slot (PLAN §12.2: "sub-types inherit a tint of the parent"). */
export const effectiveColorSlot = (types: readonly EntityTypeDef[], typeId: string): number | null =>
  typeChain(types, typeId).find(t => t.colorSlot !== null)?.colorSlot ?? null;

/** Would making `parentId` the parent of `typeId` create a loop? */
export const wouldCycle = (types: readonly EntityTypeDef[], typeId: string, parentId: string): boolean =>
  typeChain(types, parentId).some(t => t.id === typeId);

/** A template's blocks after inheritance: parent blocks, minus removed ones, overridden/added by key, in order. */
export function resolveTemplateBlocks(templates: readonly ProfileTemplateDef[], templateId: string, depth = 0): TemplateBlock[] {
  const t = templates.find(x => x.id === templateId);
  if (!t || depth > 8) return [];
  const inherited = t.parentTemplateId ? resolveTemplateBlocks(templates, t.parentTemplateId, depth + 1) : [];
  const byKey = new Map(inherited.filter(b => !t.removedBlocks.includes(b.key)).map(b => [b.key, b]));
  for (const b of t.blocks) byKey.set(b.key, { ...byKey.get(b.key), ...b });
  return [...byKey.values()].sort((a, b) => a.order - b.order);
}

/** The template that lays out this type: the nearest type in the chain with an active template. */
export function activeTemplateFor(snap: Snap, typeId: string): ProfileTemplateDef | null {
  for (const t of typeChain(snap.types, typeId)) {
    const tpl = snap.templates.find(x => x.entityTypeId === t.id && x.active);
    if (tpl) return tpl;
  }
  return null;
}

export interface ResolvedGroup {
  key: string;
  label: string;
  fields: FieldDef[];
}

/** Field groups for a type, inherited root-first; a sub-type's group with the same key adds fields to it. */
export function groupsForType(snap: Snap, typeId: string, { includeArchived = false } = {}): ResolvedGroup[] {
  const chain = typeChain(snap.types, typeId).reverse();
  const out = new Map<string, ResolvedGroup & { order: number }>();
  for (const t of chain) {
    const groups = snap.groups.filter(g => g.entityTypeId === t.id).sort((a, b) => a.order - b.order);
    for (const g of groups) {
      const fields = snap.fields.filter(f => f.groupId === g.id && (includeArchived || !f.archived)).sort((a, b) => a.order - b.order);
      const existing = out.get(g.key);
      if (existing) existing.fields.push(...fields);
      else out.set(g.key, { key: g.key, label: g.label, order: out.size, fields });
    }
  }
  return [...out.values()].map(({ order: _order, ...g }) => g);
}

export interface ResolvedBlock extends TemplateBlock {
  fields: FieldDef[];
}

export interface ResolvedProfile {
  type: EntityTypeDef;
  chain: EntityTypeDef[];
  colorSlot: number | null;
  template: ProfileTemplateDef | null;
  /** Visible blocks in order, each with the fields of its group (ignored blocks are left out). */
  blocks: ResolvedBlock[];
  /** Groups no block shows, so no field is ever hidden (rendered under "Other fields"). */
  otherGroups: ResolvedGroup[];
}

/** Everything a profile page, importer or exporter needs to lay out an entity of this type. */
export function resolveProfile(snap: Snap, typeId: string): ResolvedProfile | null {
  const chain = typeChain(snap.types, typeId);
  const type = chain[0];
  if (!type) return null;
  const template = activeTemplateFor(snap, typeId);
  const groups = groupsForType(snap, typeId);
  const blocks = template ? resolveTemplateBlocks(snap.templates, template.id).filter(b => !b.ignored) : [];
  const used = new Set(blocks.map(b => b.group).filter(Boolean));
  return {
    type,
    chain,
    colorSlot: effectiveColorSlot(snap.types, typeId),
    template,
    blocks: blocks.map(b => ({ ...b, fields: groups.find(g => g.key === b.group)?.fields ?? [] })),
    otherGroups: groups.filter(g => !used.has(g.key) && g.fields.length),
  };
}

/** Field keys used anywhere in a type's family (ancestors and descendants): a new key must not collide. */
export function fieldKeysInFamily(snap: Pick<Snap, 'types' | 'groups' | 'fields'>, typeId: string): Set<string> {
  const family = new Set(typeChain(snap.types, typeId).map(t => t.id));
  for (const t of snap.types) if (typeChain(snap.types, t.id).some(a => a.id === typeId)) family.add(t.id);
  const groupIds = new Set(snap.groups.filter((g: FieldGroupDef) => family.has(g.entityTypeId)).map(g => g.id));
  return new Set(snap.fields.filter(f => groupIds.has(f.groupId)).map(f => f.key));
}

/** Next minor version: "2.0" → "2.1". */
export const nextTemplateVersion = (versions: readonly string[]): string => {
  const [maj, min] = versions
    .map(v => v.split('.').map(Number) as [number, number])
    .sort((a, b) => a[0] - b[0] || a[1] - b[1])
    .at(-1) ?? [1, -1];
  return `${maj}.${min + 1}`;
};

// ─── ID codes ────────────────────────────────────────────────────────────────

/** The type an ID code points at, by its admin-configured prefix (`MED_FRANCTIREUR` → Media). Longest prefix wins. */
export function typeForCode(types: readonly EntityTypeDef[], code: string): EntityTypeDef | null {
  const c = code.trim().toUpperCase();
  return (
    types
      .filter(t => !t.archived && t.codePrefix && c.startsWith(t.codePrefix))
      .sort((a, b) => (b.codePrefix?.length ?? 0) - (a.codePrefix?.length ?? 0))[0] ?? null
  );
}

/**
 * Suggests an ID code in the program's style: `MED_FRANCTIREUR` for "Franc-Tireur";
 * people as surname + initials, `POL_WOITCHIK_A` for "Alain Woitchik".
 */
export function suggestCode(prefix: string, name: string, { person = false } = {}): string {
  const words = foldText(name)
    .toUpperCase()
    .split(' ')
    .filter(w => w && !/^(THE|LE|LA|LES|DE|DU|DES|L|D|OF|AND|ET)$/.test(w));
  if (!words.length) return prefix;
  const body =
    person && words.length > 1
      ? `${words.at(-1)}_${words
          .slice(0, -1)
          .map(w => w[0])
          .join('')}`
      : words.join('');
  return `${prefix}${body}`.slice(0, 40);
}
